import {Component, inject} from '@angular/core';
import {filter, map, take} from 'rxjs/operators';
import {EagleILayer} from '../../classes/eagle-i-layer';
import {CustomPopover} from '../../enum/custom-popover';
import {LayerService} from '../../services/layer.service';
import {SystemEventService} from '../../../system-event/services/system-event.service';
import {FormControl, FormGroup} from '@angular/forms';
import {MapsterSource} from '../../sources/mapster-source';
import {Mapster, MapsterProperties} from '../../classes/mapster';
import {FeatureCollection, MultiPolygon, Point} from 'geojson';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MapsterTileSource} from '../../sources/mapster-tile-source';
import {GenLegendType} from '../../../../../../generated/serverModels/GenLegendType';
import {GroupedSystemEvents} from '../../../system-event/classes/grouped-system-events';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SocketConnectionService} from '../../../../../shared/services/socket-connection.service';
import {SystemNotification} from '../../../../classes/system-notification';
import {GenNotificationType} from '../../../../../../generated/serverModels/GenNotificationType';
import {GenMapsterNotification} from '../../../../../../generated/serverModels/GenMapsterNotification';
import {LeafletFeature} from '../../classes/leaflet-feature';
import {GenSystemEventAnalyticHandle} from '../../../../../../generated/serverModels/GenSystemEventAnalyticHandle';

@Component({
    selector: 'eaglei-mapster-layer',
    templateUrl: './mapster-layer.component.html',
    styleUrls: ['../layers.scss', './mapster-layer.component.scss'],
    standalone: false,
})
export class MapsterLayerComponent extends EagleILayer<MapsterSource> {
    private popup = inject(MatSnackBar);
    private layerService = inject(LayerService);
    private systemEventService = inject(SystemEventService);
    private notificationService = inject(SocketConnectionService);
    public mapsterEvents: GroupedSystemEvents;
    public eventSelectionForm: FormGroup = new FormGroup<{event: FormControl<number>}>({
        event: new FormControl<number>(null),
    });
    private features: FeatureCollection<MultiPolygon | Point, Mapster>;
    private eventId: number;
    private minZoomLevelForTiling = 16;
    private imageSources: MapsterTileSource[] = [];

    constructor() {
        super();

        this.notificationService.newSystemNotification
            .pipe(
                filter((notification) => !!notification), // using this filter in case the update is ever just undefined
                filter((notification: SystemNotification) => {
                    const mapsterData: GenMapsterNotification = notification.info as any; // TODO make a generic
                    return (
                        /*
 A few things are being checked in this filter, in order
 1. The layer needs to be on
 2. The notification needs to be from mapster
 3. We only care if the active event matches the updated event because when we change the event in the
 select we would just load the changes
 4. We only want to do this id the image is not already in the list of image sources;
 */
                        this.isActive &&
                        notification.type === GenNotificationType.MAPSTER_TILE &&
                        this.eventId === mapsterData.eventId &&
                        this.imageSources.find((source) => source.imageId === mapsterData.imageUUID) === undefined
                    );
                }),
                map((notification: SystemNotification) => notification.info),
                takeUntilDestroyed()
            )
            .subscribe((mapsterData: GenMapsterNotification) => {
                const newSource = new MapsterTileSource(mapsterData.imageUUID, this.mapLayer);
                this.imageSources.push(newSource);

                if (this.layerSource.mapRef.getZoom() >= this.minZoomLevelForTiling) {
                    newSource.addToMap();
                }

                this.layerSource.getLeafletFeatures().forEach((lf: LeafletFeature<Mapster>) => {
                    if (lf.properties.mapsterId === mapsterData.imageUUID) {
                        lf.properties.isTiled = true;
                    }
                });
            });

        this.eventSelectionForm.controls.event.valueChanges.pipe(takeUntilDestroyed()).subscribe((event: number) => {
            this.eventId = event;
            this.layerSource.removeFromMap();
            this.getMapsterData();
        });
    }

    afterViewInit(): void {
        this.mapLayer.customPopoverId = CustomPopover.MAPSTER;
        this.mapLayer.legend.type = GenLegendType.HARD_CODED;

        this.systemEventService
            .getGroupedEvents()
            .pipe(
                take(1),
                // uncomment when saving a mapster analytic works on the event management page
                map((events: GroupedSystemEvents) => {
                    return {
                        active: events.active.filter((e) => e.hasAnalytic(GenSystemEventAnalyticHandle.MAPSTER)),
                        inactive: events.inactive.filter((e) => e.hasAnalytic(GenSystemEventAnalyticHandle.MAPSTER)),
                    };
                })
            )
            .subscribe((events) => {
                this.mapsterEvents = events;
            });
    }

    toggleLayer(activeState: boolean): void {
        this.isActive = activeState;
        if (activeState) {
            if (!this.layerSource) {
                this.layerSource = new MapsterSource(this.mapLayer);
                this.listenToZoom();
            }
            if (this.eventSelectionForm.controls.event.value) {
                this.eventSelectionForm.controls.event.updateValueAndValidity();
            }
        } else {
            this.layerSource.removeFromMap();
            this.imageSources.forEach((source) => source.removeFromMap());
        }
    }

    private getMapsterData(): void {
        if (!this.eventId) {
            this.layerSource.removeFromMap();
            return;
        }

        const url = `${this.mapLayer.serviceurl}/layer/${this.eventId}`;

        this.mapLayer.startLoading();

        this.imageSources = [];

        this.layerService
            .getJsonFromUrl(url)
            .pipe(
                filter((fc: FeatureCollection<MultiPolygon | Point, MapsterProperties | Mapster>) => {
                    if (!fc) {
                        this.popup.open('There was an issue fetching data for this event', 'X', {
                            duration: 5000,
                            panelClass: ['failure'],
                        });
                        this.mapLayer.endLoading();
                        return false;
                    }

                    if (!fc.features.length) {
                        this.popup.open('Mapster does not have any imagery for this event', 'X', {
                            duration: 5000,
                            panelClass: ['failure'],
                        });
                        this.mapLayer.endLoading();
                        return false;
                    }
                    return true;
                }),
                map((fc: FeatureCollection<MultiPolygon | Point, MapsterProperties | Mapster>) => {
                    fc.features.forEach((feature) => {
                        feature.properties = new Mapster(feature.properties as MapsterProperties);
                        if (feature.properties.isTiled) {
                            this.imageSources.push(new MapsterTileSource(feature.properties.mapsterId, this.mapLayer));
                        }
                    });
                    return fc as FeatureCollection<MultiPolygon | Point, Mapster>;
                })
            )
            .subscribe({
                next: (features) => {
                    this.features = features;

                    if (this.layerSource.mapRef.getZoom() >= this.minZoomLevelForTiling) {
                        this.showImageTiles();
                    } else {
                        this.showPolygons();
                        this.layerSource.fitToFeatures(undefined, {maxZoom: this.minZoomLevelForTiling - 1});
                    }
                },
                error: (e) => {
                    this.popup.open('Mapster does not have any imagery for this event', 'X', {
                        duration: 5000,
                        panelClass: ['failure'],
                    });
                    this.mapLayer.endLoading();
                },
            });
    }

    // Switch between a normal Mapster Source and the Mapster Tile Source when we hit the minZoomLevelForTiling
    private listenToZoom() {
        this.layerSource.mapRef.on('zoomend', (zoomEvent) => {
            if (zoomEvent.target.getZoom() < this.minZoomLevelForTiling) {
                this.showPolygons();
            } else if (zoomEvent.target.getZoom() >= this.minZoomLevelForTiling) {
                this.showImageTiles();
            }
        });
    }

    private showImageTiles(): void {
        this.layerSource.removeFromMap();

        this.imageSources.forEach((source) => {
            source.fetchImages();
            source.addToMap();
        });
    }

    private showPolygons(): void {
        this.imageSources.forEach((source) => {
            source.removeFromMap();
        });

        this.layerSource.processFeatures(this.features);
        this.layerSource.addToMap();
    }

    public changeOpacity(value?: number) {
        if (!this.isActive || value === undefined) return;
        this.opacity = value;
        const zoomLevel = this.layerSource.mapRef.getZoom();
        if (zoomLevel < this.minZoomLevelForTiling) {
            this.layerSource.changeOpacity(value / 100);
        } else if (zoomLevel >= this.minZoomLevelForTiling) {
            this.imageSources.forEach((source) => {
                source.changeOpacity(value / 100);
            });
        }
    }
}
